import React from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { useIntl } from 'ha/i18n';
import { Grid, Link, units, Typography } from '@ha/dsl';
import { SearchFiltersContext } from 'ha/pages/Search/SearchFiltersContext';
import { RadiusValue, SearchAreaValue } from 'ha/types/SearchFilters';
import { useServices } from 'ha/services';
import { GlobalState } from 'ha/types/store';

import {
  getCityLocalized,
  shouldUseMapBounds,
  getNumberOfListings,
  getSearchParams,
  getCurrencyRates,
  getCachedUpdateSearch,
} from '../selectors';
import { getRadiusFilter } from '../selectors/filterSelectors';
import { getFilterBuilderContext } from '../selectors/algoliaSelectors';
import { getAlgoliaListingsQuery } from '../utils';
import { ReqParams } from '../utils/algoliaQueries/types';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: units.rem(2),
  },
  label: {
    fontSize: units.rem(0.875),
  },
  infoLabel: {
    fontSize: units.rem(0.875),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '300px',
    },
  },
}));

const MINIMUM_NUMBER_OF_LISTINGS = 5;

export const SearchResultsUseMapFilterNotification: React.FC = () => {
  const { T } = useIntl();
  const classes = useStyles();

  const [showNotification, setShowNotification] = React.useState(false);

  const localizedCityName = useSelector(getCityLocalized);
  const radiusFilterValue = useSelector(getRadiusFilter);
  const useMapBounds = useSelector(shouldUseMapBounds);
  const numberOfListings = useSelector(getNumberOfListings);

  const services = useServices();
  const reqParams = useSelector(getSearchParams);
  const rates = useSelector(getCurrencyRates);
  const { latitude, longitude, radius } =
    useSelector(getCachedUpdateSearch) || {};

  const filterContextSelector = React.useCallback(
    (state: GlobalState) => getFilterBuilderContext(state, 'listings'),
    [],
  );
  const filterBuilderContext = useSelector(filterContextSelector);

  const { onChangeSearchAreaFilter } = React.useContext(SearchFiltersContext);

  React.useEffect(() => {
    if (
      useMapBounds &&
      numberOfListings < MINIMUM_NUMBER_OF_LISTINGS &&
      latitude &&
      longitude &&
      radius
    ) {
      // call algolia in order to get the number of listings without the
      // "use map as search area" filter applied and show the notification
      // if they are more than the MINIMUM_NUMBER_OF_LISTINGS const
      services.algolia
        .searchListings([
          getAlgoliaListingsQuery(
            reqParams as ReqParams,
            {
              latitude,
              longitude,
              radius,
            },
            rates,
            filterBuilderContext,
            { withoutData: true, withoutFacets: true },
          ),
        ])
        .then(({ results }) => {
          if (results[0].nbHits > MINIMUM_NUMBER_OF_LISTINGS) {
            setShowNotification(true);
          }
        });
    } else {
      setShowNotification(false);
    }
  }, [useMapBounds, numberOfListings]);

  const handleOnClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      onChangeSearchAreaFilter([SearchAreaValue.CityBounds]);
    },
    [onChangeSearchAreaFilter],
  );

  if (!showNotification) {
    return null;
  }

  return (
    <Grid
      container
      className={classes.container}
      direction="column"
      data-test-locator="Search Results Use Map Filter Notification"
    >
      <Grid item>
        <Typography.Paragraph className={classes.infoLabel}>
          {`${
            radiusFilterValue === RadiusValue.DEFAULT
              ? T('search.map_as_filter.reset_title', localizedCityName)
              : T('search.map_as_filter.reset_keep_distance.title')
          } `}
          {T('search.map_as_filter.reset.1_zoom')}
          <Link
            to=""
            kind="mint-underline"
            onClick={handleOnClick}
            data-test-locator="Use Map As Search Area Link"
          >
            {`${T('search.map_as_filter.reset.2_link')} `}
          </Link>
        </Typography.Paragraph>
      </Grid>
    </Grid>
  );
};

export default SearchResultsUseMapFilterNotification;
